import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent } from '@theme/events';
import { StyleboardSelectorDialog } from '@theme/styleboard-selector-modal';
import { notifyWithCountdown } from '@theme/notification';
import { StyleboardRemoveDialog } from '@theme/styleboard-remove-modal';

const STYLEBOARDS_API = `https://${window.location.host}/apps/jyapi`;

/**
 * Styleboards for this page, fetched once. `loaded` separates "this shopper has no boards"
 * from "we could not reach the API", so a network blip never discards a good remembered id.
 *
 * @typedef {{ id: string, items: Array<{ style: string, color?: string | null }> }} StyleboardSummary
 * @type {Promise<{ loaded: boolean, boards: Array<StyleboardSummary> }> | null}
 */
let styleboardsCachePromise = null;

/**
 * Backward-compatible token attachment (guest + customer proxy params).
 * @param {URL} url
 */
function applyStyleboardTokens(url) {
  if (typeof StyleboardSelectorDialog?.applySessionTokensToUrl === 'function') {
    StyleboardSelectorDialog.applySessionTokensToUrl(url);
    return;
  }

  const sessionToken =
    typeof StyleboardSelectorDialog?.getSessionToken === 'function'
      ? StyleboardSelectorDialog.getSessionToken()
      : null;

  if (!sessionToken) return;

  if (url.pathname.includes('/retail/styleboards-list')) {
    url.searchParams.append('ids[]', sessionToken);
    return;
  }

  url.searchParams.set('session_token', sessionToken);
}

/**
 * Fetches CSRF token from server (required for POSTs).
 * @returns {Promise<string | null>}
 */
async function getCsrfToken() {
  try {
    const response = await fetch(`${STYLEBOARDS_API}/csrf-token`, {
      method: 'GET',
      credentials: 'include',
      headers: { accept: 'application/json' },
    });
    if (!response.ok) return null;
    const data = await response.json().catch(() => ({}));
    return data?.token ?? data?.csrf_token ?? data?.csrfToken ?? data?._token ?? null;
  } catch {
    return null;
  }
}

/** Invalidates the styleboards cache so the next check fetches fresh data (e.g. after add/remove). */
function invalidateStyleboardsCache() {
  styleboardsCachePromise = null;
}

/**
 * @returns {string}
 */
function buildStyleboardsUrl() {
  const url = new URL(`${STYLEBOARDS_API}/retail/styleboards-list`);
  if (typeof StyleboardSelectorDialog?.applySessionTokensToUrl === 'function') {
    StyleboardSelectorDialog.applySessionTokensToUrl(url);
  }
  return url.toString();
}

/**
 * A custom element that handles product actions like adding to styleboard.
 */
class ProductActions extends Component {
  /** @type {HTMLElement | Document | null} */
  #variantUpdateTarget = null;

  /** @type {any | null} */
  #currentVariant = null;

  /** @type {Array<{ id: string, name: string, itemId?: number }>} */
  #matchingStyleboards = [];

  /** @type {boolean} */
  #isInStyleboard = false;

  /**
   * True while a click is being handled. The add runs behind a five second countdown,
   * so without this a second click starts a second countdown and a second add.
   * @type {boolean}
   */
  #isHandlingClick = false;

  /**
   * Gets the styleboard selector dialog component.
   * @returns {{ showWithProduct: (product: Object) => void } | null} The dialog component or null if not found
   */
  #getStyleboardSelectorDialog() {
    const element = document.getElementById('styleboard-selector-dialog');
    return /** @type {{ showWithProduct: (product: Object) => void } | null} */ (element);
  }

  /**
   * Gets the selected color value from the variant picker.
   * Supports both the color radio buttons and the hidden checkbox input.
   * @param {Element} variantPicker - The variant picker element
   * @returns {string | null} The selected color value or null
   */
  #getSelectedColor(variantPicker) {
    const selectedRadio =
      variantPicker.querySelector('input[type="radio"][name^="Color-"]:checked') ||
      variantPicker.querySelector('input[type="radio"][name^="color-"]:checked');

    const hiddenInput =
      variantPicker.querySelector('input[type="checkbox"][name="options[Color]"]') ||
      variantPicker.querySelector('input[type="checkbox"][name="options[color]"]');

    const input =
      (selectedRadio instanceof HTMLInputElement && selectedRadio.value && selectedRadio) ||
      (hiddenInput instanceof HTMLInputElement && hiddenInput.value && hiddenInput);

    if (!input || !(input instanceof HTMLInputElement)) return null;

    const value = input.dataset.colorCode ?? input.value;
    return value.startsWith('MTO-') ? value.substring(4) : value;
  }

  /**
   * Gets the selected type value from the variant picker and converts it to ecom/mto format.
   * @param {Element} variantPicker - The variant picker element
   * @returns {string} The type value ('ecom' or 'mto')
   */
  #getSelectedType(variantPicker) {
    // Try to get type from hidden checkbox input by name attribute
    const typeInput =
      variantPicker.querySelector('input[type="checkbox"][name="options[Type]"]') ||
      variantPicker.querySelector('input[type="checkbox"][name="options[type]"]');

    if (typeInput instanceof HTMLInputElement && typeInput.value) {
      const typeValue = typeInput.value.toLowerCase().trim();
      if (typeValue === 'made to order' || typeValue === 'mto') {
        return 'mto';
      }
      if (typeValue === 'ready to ship' || typeValue === 'rts') {
        return 'ecom';
      }
    }

    return 'ecom';
  }

  /**
   * Fetches styleboards with items (cached per page).
   * @returns {Promise<{ loaded: boolean, boards: Array<StyleboardSummary> }>}
   */
  async #fetchStyleboardsWithItems() {
    if (!styleboardsCachePromise) {
      styleboardsCachePromise = fetch(buildStyleboardsUrl(), {
        method: 'GET',
        credentials: 'include',
        headers: { accept: 'application/json' },
      })
        .then(async (r) =>
          r.ok
            ? { loaded: true, boards: (await r.json()).data?.styleboards || [] }
            : { loaded: false, boards: [] }
        )
        .catch(() => ({ loaded: false, boards: [] }));
    }
    return styleboardsCachePromise;
  }

  /**
   * Checks whether a remembered styleboard id still belongs to this shopper.
   * Boards get deleted while the id lives on in localStorage, and adding to a
   * deleted board fails with a 404 the shopper cannot act on.
   * @param {string} styleBoardId
   * @returns {Promise<boolean>}
   */
  async #isStyleboardStillAvailable(styleBoardId) {
    const { loaded, boards } = await this.#fetchStyleboardsWithItems();

    // Could not read the list — let the add request itself be the judge rather
    // than sending the shopper to an empty picker.
    if (!loaded) return true;

    return boards.some((board) => String(board.id) === String(styleBoardId));
  }

  /**
   * Checks if product (style + color) is in any styleboard.
   * @param {string} style
   * @param {string | null | undefined} color
   * @returns {Promise<boolean>}
   */
  async #isProductInStyleboard(style, color) {
    if (!style) return false;
    const { boards } = await this.#fetchStyleboardsWithItems();
    const colorNorm = color ? String(color).toUpperCase().trim() : null;
    for (const board of boards) {
      const items = board.items || [];
      for (const item of items) {
        const itemStyle = item.style;
        const itemColor = item.color ? String(item.color).toUpperCase().trim() : null;
        const styleMatch = itemStyle === style;
        const colorMatch = !colorNorm && !itemColor ? true : colorNorm === itemColor;
        if (styleMatch && colorMatch) return true;
      }
    }
    return false;
  }

  /**
   * Updates the styleboard button visual state.
   * @param {HTMLButtonElement} button
   * @param {boolean} inStyleboard
   */
  #updateStyleboardButtonState(button, inStyleboard) {
    if (inStyleboard) {
      button.classList.add('sb-in-styleboard');
    } else {
      button.classList.remove('sb-in-styleboard');
    }
  }

  /**
   * Gets color code from variant SKU.
   * Expected SKU format: PREFIX-STYLE-COLOR-SIZE (example: _MTO-22561B-FRV-00)
   * @param {Object} variant - Variant object with sku
   * @param {HTMLElement} button - (unused) kept for backward signature
   * @returns {string | null}
   */
  #getColorFromVariant(variant, button) {
    void button;
    const sku = /** @type {any} */ (variant)?.sku;
    if (typeof sku !== 'string' || !sku.trim()) return null;
    const parts = sku.split('-').filter(Boolean);
    if (parts.length < 3) return null;
    const color = parts[parts.length - 2];
    return color ? String(color).toUpperCase().trim() : null;
  }

  /**
   * Gets the current color for the styleboard button (from data attribute or variant picker).
   * @param {HTMLButtonElement} button
   * @returns {string | null}
   */
  #getButtonColor(button) {
    let color = button.dataset.color;
    if (color) return color.toUpperCase();

    const productId = button.dataset.productId;
    const variantPicker =
      this.closest('variant-picker') ||
      (productId
        ? document.querySelector(`variant-picker[data-product-id="${productId}"]`)
        : null) ||
      this.closest('form')?.closest('variant-picker');

    if (variantPicker) {
      const colorValue = this.#getSelectedColor(variantPicker);
      if (colorValue) return colorValue.toUpperCase();
    }

    return null;
  }

  /**
   * Checks and updates styleboard button state for this product.
   */
  async #checkStyleboardState() {
    const button = this.querySelector('[data-action="add-to-style-board"]');
    if (!button || !(button instanceof HTMLButtonElement)) return;

    const style = button.dataset.style ?? '';
    const color = this.#getButtonColor(button);

    const inStyleboard = await this.#isProductInStyleboard(style, color);
    this.#updateStyleboardButtonState(button, inStyleboard);
  }

  /**
   * @param {CustomEvent} e
   */
  #handleProductAddedToStyleboard = (e) => {
    const { product } = e.detail || {};
    if (!product) return;

    invalidateStyleboardsCache();

    const button = this.querySelector('[data-action="add-to-style-board"]');
    if (!button || !(button instanceof HTMLButtonElement)) return;

    const style = button.dataset.style;
    const btnColorNorm = this.#getButtonColor(button);
    const prodStyle = product.style;
    const prodColorNorm = product.color ? String(product.color).toUpperCase().trim() : null;
    const colorMatch = (prodColorNorm || null) === (btnColorNorm || null);
    if (style === prodStyle && colorMatch) {
      this.#updateStyleboardButtonState(button, true);
    }
  };

  /**
   * @param {CustomEvent} e
   */
  #handleProductRemovedFromStyleboard = (e) => {
    const { product } = e.detail || {};
    if (!product) return;

    invalidateStyleboardsCache();

    const button = this.querySelector('[data-action="add-to-style-board"]');
    if (!button || !(button instanceof HTMLButtonElement)) return;

    const style = button.dataset.style;
    const btnColorNorm = this.#getButtonColor(button);
    const prodStyle = product.style;
    const prodColorNorm = product.color ? String(product.color).toUpperCase().trim() : null;
    const colorMatch = (prodColorNorm || null) === (btnColorNorm || null);
    if (style === prodStyle && colorMatch) {
      this.#updateStyleboardButtonState(button, false);
    }
  };

  /**
   * Updates the data-color and data-type attributes on the add-to-styleboard button when variant changes.
   * Uses variant from event for color (avoids DOM timing issues after section morph).
   * @param {VariantUpdateEvent} event - The variant update event
   */
  #handleVariantUpdate = (event) => {
    const button = this.querySelector('[data-action="add-to-style-board"]');
    if (!button || !(button instanceof HTMLElement)) return;

    const productId = button.dataset.productId;
    const eventProductId = event.detail?.data?.productId;

    if (productId && eventProductId && productId !== eventProductId) {
      return;
    }

    const variant = event.detail?.resource;
    if (!variant) return;

    // Cache current variant so click handlers can use SKU-derived codes.
    this.#currentVariant = variant;

    // Prefer color from variant (option1/option2/option3) - reliable after morph when DOM may not be ready
    const colorFromVariant = this.#getColorFromVariant(variant, button);
    if (colorFromVariant) {
      button.setAttribute('data-color', colorFromVariant);
    } else {
      const variantPicker =
        this.closest('variant-picker') ||
        (productId
          ? document.querySelector(`variant-picker[data-product-id="${productId}"]`)
          : null) ||
        this.closest('form')?.closest('variant-picker');
      const colorValue = variantPicker ? this.#getSelectedColor(variantPicker) : null;
      if (colorValue) {
        button.setAttribute('data-color', colorValue.toUpperCase());
      } else {
        button.removeAttribute('data-color');
      }
    }

    const variantPicker =
      this.closest('variant-picker') ||
      (productId
        ? document.querySelector(`variant-picker[data-product-id="${productId}"]`)
        : null) ||
      this.closest('form')?.closest('variant-picker');
    if (variantPicker) {
      const typeValue = this.#getSelectedType(variantPicker);
      button.setAttribute('data-type', typeValue);
    }

    if (variant.id) {
      button.setAttribute('data-variant-id', variant.id);
    }

    this.#checkStyleboardState();
  };

  /**
   * Shows the styleboard selector modal.
   * @param {{ style: string, color?: string | null, type: string, productId?: string, productHandle?: string, variantId?: string | null }} product - Product data
   */
  #showStyleBoardSelector(product) {
    const dialog = this.#getStyleboardSelectorDialog();

    if (!dialog) {
      console.error(
        'Styleboard selector dialog not found. Make sure styleboard-selector-modal.liquid is rendered in the layout.'
      );
      const styleboardsUrl = '/apps/jyapi/retail/styleboards';
      if (
        confirm(
          'Please select a styleboard to add this product to. Would you like to go to your styleboards page?'
        )
      ) {
        window.location.href = styleboardsUrl;
      }
      return;
    }

    if (typeof dialog.showWithProduct === 'function') {
      dialog.showWithProduct(product);
    } else {
      console.error('Styleboard selector dialog does not have showWithProduct method');
    }
  }

  /**
   * Automatically adds a product to a styleboard without opening selector dialog.
   * @param {string} styleBoardId
   * @param {{ style: string, color?: string | null, type: string, productId?: string, productHandle?: string, variantId?: string | null }} product
   */
  async #autoAddToStyleBoard(styleBoardId, product) {
    const notify = /** @type {any} */ (window).notification;
    try {
      const url = new URL(`${STYLEBOARDS_API}/retail/styleboards/${styleBoardId}/items`);
      applyStyleboardTokens(url);

      const csrfToken = await getCsrfToken();
      const body = {
        style: product.style,
        color: product.color || null,
        type: product.type,
        variant_id: product.variantId || null,
        note: null,
        _token: csrfToken,
      };

      const doRequest = async (isRetry = false) => {
        const response = await fetch(url.toString(), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            accept: 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify(body),
        });

        if (response.status === 419 && !isRetry) {
          const fresh = await getCsrfToken();
          if (fresh) {
            body._token = fresh;
          }
          return doRequest(true);
        }

        return response;
      };

      const response = await doRequest();

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        // A 404 means the remembered board is gone, which is not something the
        // shopper can act on — say what actually happened instead.
        const message =
          response.status === 404
            ? 'That style board is no longer available. Pick another one below.'
            : errorData.message || 'Failed to add product to styleboard';
        throw new Error(message);
      }

      if (typeof StyleboardSelectorDialog?.setLastStyleboardId === 'function') {
        StyleboardSelectorDialog.setLastStyleboardId(styleBoardId);
      }

      if (notify && typeof notify.success === 'function') {
        notify.success('Product added to styleboard!');
      }

      invalidateStyleboardsCache();

      document.dispatchEvent(
        new CustomEvent('product-added-to-styleboard', {
          detail: { styleBoardId, product },
        })
      );
    } catch (error) {
      console.error('Error auto-adding to styleboard:', error);
      if (notify && typeof notify.error === 'function') {
        notify.error('Failed to add product', error?.message || 'Please try again');
      }
      if (typeof StyleboardSelectorDialog?.clearLastStyleboardId === 'function') {
        StyleboardSelectorDialog.clearLastStyleboardId();
      }
      // The cached list is what told us this board was usable, so drop it before
      // reopening the picker — otherwise the deleted board is still offered, and
      // every later availability check on this page reuses the same stale answer.
      invalidateStyleboardsCache();
      this.#showStyleBoardSelector(product);
    }
  }

  /**
   * Gets the styleboard remove dialog component.
   * @returns {StyleboardRemoveDialog | null}
   */
  #getStyleboardRemoveDialog() {
    return /** @type {StyleboardRemoveDialog | null} */ (
      document.querySelector('styleboard-remove-dialog')
    );
  }

  /**
   * Opens remove dialog for this product.
   * @param {{ style: string, color?: string | null, type: string }} product
   */
  #showRemoveDialog(product) {
    const dialog = this.#getStyleboardRemoveDialog();
    if (!dialog) {
      console.error(
        'Remove dialog not found. Make sure styleboard-remove-modal.liquid is rendered in the layout.'
      );
      return;
    }
    dialog.showWithProduct(product);
  }

  /**
   * Handles the add to styleboard button click.
   * @param {Event} event - The click event
   */
  handleAddToStyleBoard = async (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const button = target.closest(
      '[data-action="add-to-style-board"], [data-action="remove-from-style-board"]'
    );
    if (!(button instanceof HTMLElement)) return;

    event.preventDefault();
    event.stopPropagation();

    // The add sits behind a five second countdown, so a repeat click during that
    // window would start a second countdown and a second add for the same product.
    if (this.#isHandlingClick) return;
    this.#isHandlingClick = true;

    try {
      await this.#processStyleboardClick(button);
    } finally {
      this.#isHandlingClick = false;
    }
  };

  /**
   * Resolves the current variant selection and either adds to the remembered
   * styleboard, opens the picker, or opens the remove dialog.
   * @param {HTMLElement} button - The add/remove button that was clicked
   */
  async #processStyleboardClick(button) {
    const style = button.dataset.style;
    let color = button.dataset.color;
    let type = button.dataset.type || 'ecom';
    const isRemoveAction =
      button.dataset.action === 'remove-from-style-board' ||
      button.classList.contains('sb-in-styleboard');

    // Always re-check color and type from variant picker (variant changes can update without re-render)
    const variantPicker =
      this.closest('variant-picker') ||
      document.querySelector(`variant-picker[data-product-id="${button.dataset.productId}"]`) ||
      this.closest('form')?.closest('variant-picker');

    if (variantPicker) {
      if (!color) {
        // Prefer SKU-derived color code when available
        const colorFromSku = this.#currentVariant
          ? this.#getColorFromVariant(this.#currentVariant, button)
          : null;
        const fallbackColorValue = this.#getSelectedColor(variantPicker);
        const resolved =
          colorFromSku || (fallbackColorValue ? fallbackColorValue.toUpperCase() : null);
        if (resolved) {
          color = resolved;
          button.setAttribute('data-color', resolved);
        }
      }

      // Always re-check type to ensure it's current
      const typeValue = this.#getSelectedType(variantPicker);
      type = typeValue;
      button.setAttribute('data-type', type);
    }

    if (isRemoveAction) {
      if (!style) {
        console.error('Style number is required to remove product from styleboard');
        return;
      }
      if (!color) {
        console.error('Color code is required to remove product from styleboard');
        const notify = /** @type {any} */ (window).notification;
        if (notify && typeof notify.error === 'function') {
          notify.error('Unable to remove product', 'Color code is missing');
        }
        return;
      }
      this.#showRemoveDialog({ style, color, type });
      return;
    }

    if (!style) {
      console.error('Style number is required to add product to styleboard');
      const notify = /** @type {any} */ (window).notification;
      if (notify && typeof notify.error === 'function') {
        notify.error('Unable to add product to styleboard', 'Style number is missing');
      } else {
        alert('Unable to add product to styleboard: style number is missing');
      }
      return;
    }

    const product = {
      style: style,
      color: color || null,
      type: type,
      productId: button.dataset.productId,
      productHandle: button.dataset.productHandle,
      variantId: button.dataset.variantId || null,
    };

    let lastStyleboardId =
      typeof StyleboardSelectorDialog?.getLastStyleboardId === 'function'
        ? StyleboardSelectorDialog.getLastStyleboardId()
        : null;

    // The remembered board may have been deleted since it was stored. Check it against
    // the boards we already fetched for this page before committing to the silent add,
    // otherwise the shopper waits out the countdown only to be handed a 404.
    if (lastStyleboardId && !(await this.#isStyleboardStillAvailable(lastStyleboardId))) {
      if (typeof StyleboardSelectorDialog?.clearLastStyleboardId === 'function') {
        StyleboardSelectorDialog.clearLastStyleboardId();
      }
      lastStyleboardId = null;
    }

    if (lastStyleboardId) {
      const result = await notifyWithCountdown({
        titleTemplate: 'Adding to last styleboard in {seconds}s... (click to change)',
        countdownSeconds: 5,
        type: 'info',
      });

      if (result === 'completed') {
        await this.#autoAddToStyleBoard(lastStyleboardId, product);
        return;
      }
    }

    const addToStyleBoardEvent = new CustomEvent('add-to-styleboard', {
      detail: product,
      bubbles: true,
      cancelable: true,
    });

    const dispatched = document.dispatchEvent(addToStyleBoardEvent);

    if (dispatched && !addToStyleBoardEvent.defaultPrevented) {
      this.#showStyleBoardSelector(product);
    }
  }

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('click', this.handleAddToStyleBoard);

    // Listen on document so we receive variantUpdate even after section/main morph (when variant-picker is replaced)
    this.#variantUpdateTarget = document;
    document.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);

    document.addEventListener('product-added-to-styleboard', this.#handleProductAddedToStyleboard);
    document.addEventListener(
      'product-removed-from-styleboard',
      this.#handleProductRemovedFromStyleboard
    );

    this.#checkStyleboardState();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.handleAddToStyleBoard);

    document.removeEventListener(
      'product-added-to-styleboard',
      this.#handleProductAddedToStyleboard
    );
    document.removeEventListener(
      'product-removed-from-styleboard',
      this.#handleProductRemovedFromStyleboard
    );

    const target = this.#variantUpdateTarget;
    if (target) {
      target.removeEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
      this.#variantUpdateTarget = null;
    }
  }
}

if (!customElements.get('product-actions')) {
  customElements.define('product-actions', ProductActions);
}
